import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { formatPKR } from '../utils/formatters';
import { FiTrash2, FiMinus, FiPlus, FiShoppingBag } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import ProductImage from '../components/ProductImage';
import api from '../utils/api';
import toast from 'react-hot-toast';


const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity, getCartTotal } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [applying, setApplying] = useState(false);

  const subtotal = getCartTotal();
  const shipping = subtotal >= 3000 ? 0 : 250;
  const total = subtotal + shipping - discount;

  // Auto re-validate coupon when cart changes
  React.useEffect(() => {
    if (appliedCoupon) {
      const revalidate = async () => {
        try {
          const response = await api.post('/coupons/validate', {
            code: appliedCoupon.code,
            orderTotal: subtotal,
            items: cartItems.map(item => ({
              productId: item.productId,
              price: item.price,
              quantity: item.quantity,
              category: item.category
            }))
          });
          setDiscount(response.data.coupon.discountAmount);
        } catch (error) {
          // If coupon is no longer valid (e.g. min amount not met after removal)
          toast.error(error.response?.data?.message || 'Coupon no longer valid');
          setDiscount(0);
          setAppliedCoupon(null);
          setCouponCode('');
        }
      };
      revalidate();
    }
  }, [cartItems, subtotal]);

  const handleApplyCoupon = async () => {
    if (!couponCode) return;
    setApplying(true);
    try {
      const response = await api.post('/coupons/validate', {
        code: couponCode,
        orderTotal: subtotal,
        items: cartItems.map(item => ({
          productId: item.productId,
          price: item.price,
          quantity: item.quantity,
          category: item.category
        }))
      });
      
      setDiscount(response.data.coupon.discountAmount);
      setAppliedCoupon(response.data.coupon);
      toast.success('Coupon applied successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid coupon');
      setDiscount(0);
      setAppliedCoupon(null);
    } finally {
      setApplying(false);
    }
  };

  const handleCheckout = () => {
    navigate('/checkout', { state: { couponCode: appliedCoupon?.code, discount } });
  };

  if (cartItems.length === 0) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <FiShoppingBag className="w-24 h-24 mx-auto text-gray-400 mb-4" />
        <h2 className="text-2xl font-bold mb-2">Your cart is empty</h2>
        <p className="text-gray-500 mb-6">Start shopping to add items to your cart</p>
        <Link to="/products" className="btn-primary">
          Continue Shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Shopping Cart</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-4">
          {cartItems.map((item, index) => (
            <motion.div
              key={`${item.productId}-${item.size}-${item.color}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card p-4 flex gap-4"
            >
              <ProductImage
                src={item.image}
                alt={item.name}
                className="w-24 h-24 rounded"
              />

              <div className="flex-1">
                <h3 className="font-semibold mb-1">{item.name}</h3>
                <div className="flex flex-wrap gap-2 mb-2">
                  {item.size && (
                    <span className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                      Size: {typeof item.size === 'object' ? (item.size.size || 'N/A') : item.size}
                    </span>
                  )}
                  {item.color && (
                    <span className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                      Color: {typeof item.color === 'object' ? (item.color.color || 'N/A') : item.color}
                    </span>
                  )}
                </div>
                <p className="font-bold mt-2">{formatPKR(item.price)}</p>
              </div>
              <div className="flex flex-col items-end justify-between">
                <button
                  onClick={() => removeFromCart(item.productId, item.size, item.color)}
                  className="text-red-500 hover:text-red-700"
                >
                  <FiTrash2 className="w-5 h-5" />
                </button>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => updateQuantity(item.productId, item.size, item.quantity - 1, item.color)}
                    className="p-1 border rounded"
                  >
                    <FiMinus className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center">{item.quantity}</span>
                  <button
                    onClick={() => updateQuantity(item.productId, item.size, item.quantity + 1, item.color)}
                    className="p-1 border rounded"
                  >
                    <FiPlus className="w-4 h-4" />
                  </button>
                </div>
                <p className="font-bold mt-2">
                  {formatPKR(item.price * item.quantity)}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="card p-6 sticky top-20">
            <h2 className="text-xl font-bold mb-4">Order Summary</h2>
            
            {/* Coupon Input */}
            <div className="mb-6">
              <label className="text-sm font-medium mb-2 block">Apply Coupon</label>
              <div className="flex gap-2">
                <input 
                  type="text" 
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Enter code"
                  className="input-field flex-1"
                  disabled={appliedCoupon}
                />
                {appliedCoupon ? (
                  <button 
                    onClick={() => { setAppliedCoupon(null); setDiscount(0); setCouponCode(''); }}
                    className="btn-secondary px-3"
                  >
                    Remove
                  </button>
                ) : (
                  <button 
                    onClick={handleApplyCoupon}
                    disabled={applying || !couponCode}
                    className="btn-primary px-3"
                  >
                    {applying ? '...' : 'Apply'}
                  </button>
                )}
              </div>
              {appliedCoupon && (
                <p className="text-xs text-green-500 mt-2">
                  Coupon "{appliedCoupon.code}" applied!
                </p>
              )}
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>{formatPKR(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>Shipping</span>
                <span>
                  {shipping === 0 ? (
                    <span className="text-green-500">Free</span>
                  ) : (
                    formatPKR(shipping)
                  )}
                </span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-green-500 font-medium">
                  <span>Discount</span>
                  <span>-{formatPKR(discount)}</span>
                </div>
              )}
              {subtotal < 3000 && (
                <p className="text-sm text-gray-500">
                  Add {formatPKR(3000 - subtotal)} more for free shipping
                </p>
              )}
            </div>
            <div className="border-t pt-4 mb-4">
              <div className="flex justify-between font-bold text-lg">
                <span>Total</span>
                <span>{formatPKR(total)}</span>
              </div>
            </div>
            <button onClick={handleCheckout} className="btn-primary w-full mb-4">
              Proceed to Checkout
            </button>
            <Link to="/products" className="btn-secondary w-full block text-center">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
